import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ParamMutation,
  SearchParamContext,
  SearchParamQueue,
} from './searchParamContext';

/**
 * Apply a batch of queued mutations onto a copy of the current params: `null`
 * removes the key, `[]` writes the `?key=` empty marker, and a non-empty array
 * sets exactly those values. Extracted from the flush callback to keep nesting
 * shallow.
 * @param prev - the params react-router currently holds.
 * @param mutations - the coalesced mutations, keyed by param name.
 */
const applyMutations = (
  prev: URLSearchParams,
  mutations: Map<string, ParamMutation>,
): URLSearchParams => {
  const next = new URLSearchParams(prev);
  mutations.forEach((mutation, key) => {
    next.delete(key);
    if (mutation === null) return; // remove entirely
    if (mutation.length === 0) {
      next.set(key, ''); // empty marker
    } else {
      mutation.forEach((value) => next.append(key, value));
    }
  });
  return next;
};

/**
 * Single owner of URL search-param writes for `useEncodedQueryParamV2`.
 *
 * Every hook enqueues its mutations here instead of calling `setSearchParams`
 * itself. The provider coalesces all mutations queued within a tick and flushes
 * them together on the next microtask via **one** `setSearchParams((prev) =>
 * …)`. Because there is a single writer applying every key to one `prev`
 * snapshot, concurrent restores can neither clobber each other (the race) nor
 * drop a sibling that another hook then re-restores (the loop). It reads
 * react-router's `prev`, so it behaves identically under `BrowserRouter`,
 * `MemoryRouter`, and `HashRouter` — no `window.location` dependency.
 *
 * Mount once at the app root, inside the Router.
 */
export const SearchParamProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pending = React.useRef(new Map<string, ParamMutation>());
  // Restores should not stack history, so default to replace; a single push
  // request in the batch upgrades the whole flush to a push.
  const replace = React.useRef(true);
  const scheduled = React.useRef(false);

  const flush = React.useCallback(() => {
    scheduled.current = false;
    if (pending.current.size === 0) return;
    const mutations = pending.current;
    pending.current = new Map();
    const useReplace = replace.current;
    replace.current = true;

    setSearchParams((prev) => applyMutations(prev, mutations), {
      replace: useReplace,
    });
  }, [setSearchParams]);

  const enqueue = React.useCallback<SearchParamQueue['enqueue']>(
    (key, mutation, shouldReplace) => {
      pending.current.set(key, mutation);
      if (!shouldReplace) replace.current = false;
      if (!scheduled.current) {
        scheduled.current = true;
        queueMicrotask(flush);
      }
    },
    [flush],
  );

  const value = React.useMemo<SearchParamQueue>(
    () => ({ searchParams, enqueue }),
    [searchParams, enqueue],
  );

  return (
    <SearchParamContext.Provider value={value}>
      {children}
    </SearchParamContext.Provider>
  );
};
